import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Building2,
	CheckCircle2,
	Circle,
	Eye,
	Loader2,
	Users,
} from "lucide-react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/vendors/")({
	component: VendorsPage,
});

interface VendorProfile {
	id: string;
	user_id: string;
	external_user_id: string;
	first_name: string | null;
	last_name: string | null;
	phone_number: string | null;
	company: string | null;
	external_org_id: string | null;
	external_org_name: string | null;
}

interface VendorEngagement {
	id: string;
	vendor_profile_id: string;
	first_visited_at: string;
	last_visited_at: string;
	visit_count: number;
	vendor_profiles: VendorProfile | null;
}

function VendorsPage() {
	const { user } = useAuth();
	const trpc = useTRPC();

	// Fetch vendors who have engaged with the portal
	const { data: engagementsData, isLoading } = useQuery(
		trpc.vendorManagement.engagements.list.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			limit: 100,
			offset: 0,
		}),
	);

	const vendors = (engagementsData?.engagements ?? []) as VendorEngagement[];
	const onboardingStatuses = (engagementsData?.onboardingStatuses ?? {}) as Record<string, "complete" | "in_progress" | "not_started">;

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	// Calculate stats
	const withCompany = vendors.filter((v) => v.vendor_profiles?.company || v.vendor_profiles?.external_org_name).length;
	const onboardingComplete = vendors.filter((v) => onboardingStatuses[v.vendor_profile_id] === "complete").length;

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Vendors</h1>
					<p className="text-muted-foreground">
						Vendors who have accessed your portal
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-3">
					<div className="border rounded-lg p-4">
						<div className="flex items-center gap-2">
							<Users className="h-4 w-4 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">Total Vendors</p>
						</div>
						<p className="text-2xl font-bold">{vendors.length}</p>
					</div>
					<div className="border rounded-lg p-4">
						<div className="flex items-center gap-2">
							<Building2 className="h-4 w-4 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">Companies</p>
						</div>
						<p className="text-2xl font-bold">{withCompany}</p>
					</div>
					<div className="border rounded-lg p-4">
						<div className="flex items-center gap-2">
							<CheckCircle2 className="h-4 w-4 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">Onboarding Complete</p>
						</div>
						<p className="text-2xl font-bold">{onboardingComplete}</p>
					</div>
				</div>

				{/* Table */}
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Company</TableHead>
								<TableHead>Contact</TableHead>
								<TableHead>Onboarding Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{vendors.map((engagement) => {
								const profile = engagement.vendor_profiles;
								const companyName = profile?.company || profile?.external_org_name || "Unknown Company";
								const contactName = profile
									? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || "-"
									: "-";

								// Get actual onboarding status from API response
								const onboardingStatus = onboardingStatuses[engagement.vendor_profile_id] ?? "not_started";

								return (
									<TableRow key={engagement.id}>
										<TableCell>
											<Link
												to="/vendors/$id"
												params={{ id: engagement.vendor_profile_id }}
												className="font-medium hover:underline"
											>
												{companyName}
											</Link>
										</TableCell>
										<TableCell>
											<span className="text-sm">{contactName}</span>
										</TableCell>
										<TableCell>
											{onboardingStatus === "complete" ? (
												<Badge className="bg-green-100 text-green-700">
													<CheckCircle2 className="h-3 w-3 mr-1" />
													Complete
												</Badge>
											) : onboardingStatus === "in_progress" ? (
												<Badge className="bg-yellow-100 text-yellow-700">
													<Circle className="h-3 w-3 mr-1" />
													In Progress
												</Badge>
											) : (
												<Badge variant="outline" className="text-muted-foreground">
													<Circle className="h-3 w-3 mr-1" />
													Not Started
												</Badge>
											)}
										</TableCell>
										<TableCell className="text-right">
											<Button variant="ghost" size="sm" asChild>
												<Link
													to="/vendors/$id"
													params={{ id: engagement.vendor_profile_id }}
												>
													<Eye className="h-4 w-4 mr-1" />
													View
												</Link>
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
							{vendors.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={4}
										className="text-center py-8 text-muted-foreground"
									>
										No vendors have accessed your portal yet
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</DashboardLayout>
	);
}
